import type { JSX } from "react";
import { createBrowserRouter, Navigate } from "react-router";

import {
  LoginRoute,
  RequireAgency,
  RequireAuth,
  RequirePlatformAdmin,
  RequireRole,
  RequireRoleOrPlatformAdmin,
} from "@/app/guards";
import { RouteError } from "@/app/RouteError";
import { PageLoader } from "@/components/ui";
import { ChannelsRedirect } from "@/features/settings/ChannelsRedirect";
import { SettingsIndexRedirect, SettingsLayout } from "@/features/settings/SettingsLayout";
import { AppShell } from "@/features/shell/AppShell";
import {
  canEditCrmRecords,
  canOpenBookings,
  canManageAutomations,
  canManageTenantSettings,
  canOpenAiAgent,
  canRunCampaigns,
  canUseInbox,
  canManageTeamAndDistribution,
  canManageDestinations,
  canViewContractTemplates,
  canViewCustDev,
  canViewReports,
} from "@/lib/permissions";

type PageComponent = () => JSX.Element;

/**
 * Route-level code splitting: a screen's chunk is fetched on the first visit to its
 * route, not in the entry bundle. `load` names the module and `name` the export, so a
 * renamed export fails the typecheck rather than rendering nothing.
 */
function page<K extends string>(
  load: () => Promise<Record<K, PageComponent>>,
  name: K,
): { lazy: () => Promise<{ Component: PageComponent }> } {
  return {
    lazy: async (): Promise<{ Component: PageComponent }> => {
      const module = await load();
      return { Component: module[name] };
    },
  };
}

export const router = createBrowserRouter([
  {
    // Nothing under the routes may end in React Router's developer page (see RouteError).
    errorElement: <RouteError />,
    // Shown while the first lazy route's chunk loads; without it React Router warns on
    // every cold start and renders nothing in the gap.
    hydrateFallbackElement: <PageLoader />,
    children: [
  { path: "/kirish", element: <LoginRoute /> },
  // The client's offer page. Open by link (§3.8), so outside RequireAuth and outside the shell.
  {
    path: "/p/:token",
    ...page(() => import("@/features/proposals/PublicProposalPage"), "PublicProposalPage"),
  },
  // The one-time link `create_tenant` prints. Outside RequireAuth on purpose: the
  // only person who ever follows it cannot sign in yet.
  {
    path: "/parolni-tiklash/:uid/:token",
    ...page(() => import("@/features/auth/PasswordSetPage"), "PasswordSetPage"),
  },
  // `Parolni unutdim` and the link it mails. Outside RequireAuth: nobody who needs
  // these can sign in yet.
  { path: "/forgot-password", ...page(() => import("@/features/auth/ForgotPasswordPage"), "ForgotPasswordPage") },
  {
    path: "/reset-password/:uid/:token",
    ...page(() => import("@/features/auth/ResetPasswordPage"), "ResetPasswordPage"),
  },
  // Pages a client opens from a link. No shell and no account: they sit outside RequireAuth.
  { path: "/s/:token", ...page(() => import("@/features/public/SignPage"), "SignPage") },
  { path: "/pay/:token", ...page(() => import("@/features/public/PayPage"), "PayPage") },
  { path: "/r/:token", ...page(() => import("@/features/public/ReviewPage"), "ReviewPage") },
  {
    element: <RequireAuth />,
    children: [
      {
        // Every screen under the shell is an agency's; a platform admin with no agency is sent
        // to the panel from any of them (D202).
        element: (
          <RequireAgency>
            <AppShell />
          </RequireAgency>
        ),
        children: [
          { path: "/", ...page(() => import("@/features/dashboard/DashboardPage"), "DashboardPage") },
          { path: "/profile", ...page(() => import("@/features/profile/ProfilePage"), "ProfilePage") },
          {
            // Suhbatlar: three panes on desktop, a full-screen thread on a phone. Egasi,
            // Menejer and Operator only; Moliyachi has no inbox at all.
            element: <RequireRole allow={canUseInbox} />,
            children: [
              { path: "/inbox", ...page(() => import("@/features/inbox/InboxPage"), "InboxPage") },
              // The approvals tab is gone: a draft waits inside its thread (09 §7, 4.8). Old links
              // land on the inbox filtered to the threads with a draft waiting.
              {
                path: "/inbox/approvals",
                element: <Navigate to="/inbox?state=pending_draft" replace />,
              },
              // Murojaatlar (09 §6): the free queue, longest wait first.
              {
                path: "/murojaatlar",
                ...page(() => import("@/features/inbox/MurojaatlarPage"), "MurojaatlarPage"),
              },
              {
                path: "/inbox/comments",
                ...page(() => import("@/features/inbox/CommentsPage"), "CommentsPage"),
              },
            ],
          },
          // The Meta consent callback names `/settings/channels`. Kanallar lives inside
          // `/sozlamalar`, so the redirect keeps the query (`instagram=…`) and opens that section.
          { path: "/settings/channels", element: <ChannelsRedirect /> },
          { path: "/moliya", ...page(() => import("@/features/finance/FinancePage"), "FinancePage") },
          { path: "/bitimlar", ...page(() => import("@/features/deals/DealsPage"), "DealsPage") },
          {
            path: "/bitimlar/:dealId",
            ...page(() => import("@/features/deals/DealsPage"), "DealDetailPage"),
          },
          { path: "/tasks", ...page(() => import("@/features/tasks/TasksPage"), "TasksPage") },
          { path: "/mijozlar", ...page(() => import("@/features/clients/ClientsPage"), "ClientsPage") },
          // The operator's chat-yordamchi (TZ §7.7): every member has the API, so the route is open to
          // every role the sidebar offers it to.
          {
            path: "/yordamchi",
            ...page(() => import("@/features/ai/AssistantChatPage"), "AssistantChatPage"),
          },
          // Creating a client is a CRM write (`can_edit_crm_records`): the moliyachi reads the
          // pipeline but never adds to it, so the form is refused on the route, as on the API.
          {
            element: <RequireRole allow={canEditCrmRecords} />,
            children: [
              {
                path: "/mijozlar/yangi",
                ...page(() => import("@/features/clients/NewClientPage"), "NewClientPage"),
              },
            ],
          },
          {
            path: "/hujjat-talablari",
            ...page(
              () => import("@/features/clients/DocumentRequirementsPage"),
              "DocumentRequirementsPage",
            ),
          },
          // The rules belong to the owner (Q69), the campaigns to whoever may send in bulk (D215);
          // refused on the route, so the page never asks the API for what it cannot show.
          {
            element: <RequireRole allow={canManageAutomations} />,
            children: [
              {
                ...page(() => import("@/features/ai/AiAssistantTabs"), "AiAssistantFrame"),
                children: [
                  {
                    path: "/automatizatsiya",
                    ...page(() => import("@/features/automations/AutomationsPage"), "AutomationsPage"),
                  },
                ],
              },
            ],
          },
          {
            element: <RequireRole allow={canRunCampaigns} />,
            children: [
              {
                ...page(() => import("@/features/ai/AiAssistantTabs"), "AiAssistantFrame"),
                children: [
                  {
                    path: "/kampaniyalar",
                    ...page(() => import("@/features/campaigns/CampaignsPage"), "CampaignsPage"),
                  },
                ],
              },
            ],
          },
          {
            path: "/mijozlar/:clientId",
            ...page(() => import("@/features/clients/ClientsPage"), "ClientCardPage"),
          },
          {
            path: "/tur-qidiruvi",
            ...page(() => import("@/features/search/SearchPage"), "SearchPage"),
          },
          { path: "/search", ...page(() => import("@/features/search/SearchPage"), "SearchPage") },
          // Bronlar (§10): every role but the moliyachi.
          {
            element: <RequireRole allow={canOpenBookings} />,
            children: [{ path: "/bronlar", ...page(() => import("@/features/bookings/BookingsPage"), "BookingsPage") }],
          },
          // Provider catalogue mapping and operator connections (§9.6, §10.1): the owner and Kapitan staff.
          {
            element: <RequireRoleOrPlatformAdmin allow={canManageTenantSettings} />,
            children: [
              {
                path: "/sozlamalar/katalog",
                ...page(() => import("@/features/settings/catalog/CatalogMappingPage"), "CatalogMappingPage"),
              },
              {
                path: "/sozlamalar/turoperatorlar",
                ...page(
                  () => import("@/features/settings/operators/OperatorConnectionsPage"),
                  "OperatorConnectionsPage",
                ),
              },
            ],
          },
          // Hisobotlar: the gate is the reporting roles. Each tab's own role check is in
          // `reportTabsFor`, and the API refuses the rest.
          {
            element: <RequireRole allow={canViewReports} />,
            children: [
              {
                path: "/hisobotlar",
                ...page(() => import("@/features/reports/ReportsLayout"), "ReportsLayout"),
                children: [
                  { path: "moliya", ...page(() => import("@/features/reports/ReportPages"), "MoliyaReportPage") },
                  { path: "lidlar", ...page(() => import("@/features/reports/ReportPages"), "LeadsReportPage") },
                  { path: "voronka", ...page(() => import("@/features/reports/ReportPages"), "FunnelReportPage") },
                  { path: "kanallar", ...page(() => import("@/features/reports/ReportPages"), "ChannelsReportPage") },
                  { path: "javob-tezligi", ...page(() => import("@/features/reports/ReportPages"), "ResponsesReportPage") },
                  { path: "marja", ...page(() => import("@/features/reports/ReportPages"), "MarginReportPage") },
                  { path: "qarzdorlar", ...page(() => import("@/features/reports/ReportPages"), "DebtorsReportPage") },
                  { path: "operatorlar", ...page(() => import("@/features/reports/ReportPages"), "OperatorsReportPage") },
                  { path: "ai", ...page(() => import("@/features/reports/ReportPages"), "AiReportPage") },
                  // The owner alone (§8.6), so the route carries its own gate on top of the reports one.
                  {
                    element: <RequireRole allow={canViewCustDev} />,
                    children: [
                      { path: "custdev", ...page(() => import("@/features/reports/ReportPages"), "CustDevReportPage") },
                    ],
                  },
                  { path: "filiallar", ...page(() => import("@/features/reports/ReportPages"), "BranchesReportPage") },
                ],
              },
            ],
          },
          // The personal report: every role reads its own figures, so it sits outside the gate.
          { path: "/hisobotlar/mening", ...page(() => import("@/features/reports/ReportPages"), "PersonalReportPage") },
          // Sozlamalar has its own sidebar (spec §2, §11): seven sections. The layout is open to
          // every role that has one of them; each section's route carries the gate its screen
          // needs, so a hidden link and a refused page are decided by the same predicate.
          {
            path: "/sozlamalar",
            element: <SettingsLayout />,
            children: [
              { index: true, element: <SettingsIndexRedirect /> },
              {
                element: <RequireRole allow={canManageTenantSettings} />,
                children: [
                  { path: "agentlik", ...page(() => import("@/features/settings/SettingsPage"), "SettingsPage") },
                  { path: "narx", ...page(() => import("@/features/settings/NarxSettingsPage"), "NarxSettingsPage") },
                  {
                    path: "turoperatorlar",
                    ...page(() => import("@/features/settings/placeholders"), "OperatorsSettingsPlaceholder"),
                  },
                  { path: "katalog", ...page(() => import("@/features/settings/placeholders"), "CatalogSettingsPlaceholder") },
                  {
                    path: "hujjatlar",
                    ...page(() => import("@/features/clients/DocumentRequirementsPage"), "DocumentRequirementsPage"),
                  },
                ],
              },
              {
                element: <RequireRole allow={canManageTeamAndDistribution} />,
                children: [
                  { path: "jamoa", ...page(() => import("@/features/settings/TeamSettingsPage"), "TeamSettingsPage") },
                  {
                    path: "taqsimlash",
                    ...page(() => import("@/features/settings/DistributionRulesPage"), "DistributionRulesPage"),
                  },
                ],
              },
            ],
          },
          // The agency's contract wording: whoever issues a contract reads it; only the owner edits
          // it (TZ §17.14 Shablonlar, D455). The settings registry keeps its owner-only copy.
          // The agency's destination selection (TZ §17.14 Yo'nalishlar, §1.4): the owner and the
          // branch manager curate it; the API refuses the write for anyone else.
          {
            element: <RequireRole allow={canManageDestinations} />,
            children: [
              {
                path: "/sozlamalar/yonalishlar",
                ...page(() => import("@/features/settings/DestinationSettingsPage"), "DestinationSettingsPage"),
              },
            ],
          },
          {
            element: <RequireRole allow={canViewContractTemplates} />,
            children: [
              {
                path: "/shablonlar",
                ...page(() => import("@/features/settings/ContractTemplatesPage"), "ContractTemplatesPage"),
              },
            ],
          },
          // Integration keys: the agency's own (owner only) and the platform's (platform admin).
          {
            element: <RequireRole allow={canManageTenantSettings} />,
            children: [
              {
                path: "/sozlamalar/integratsiyalar",
                ...page(() => import("@/features/integrations/IntegrationsPages"), "AgencyIntegrationsPage"),
              },
            ],
          },
          {
            element: <RequirePlatformAdmin />,
            children: [
              {
                path: "/platforma/integratsiyalar",
                ...page(() => import("@/features/integrations/IntegrationsPages"), "PlatformIntegrationsPage"),
              },
            ],
          },
          // The AI agent section. Moliyachi is refused outright; an operator's landing is
          // decided inside the screens (the posts), and every page is checked by the API.
          {
            element: <RequireRole allow={canOpenAiAgent} />,
            children: [
              {
                path: "/ai",
                ...page(() => import("@/features/ai/AiLayout"), "AiSectionLayout"),
                children: [
                  { index: true, ...page(() => import("@/features/ai/AiOverviewPage"), "AiOverviewPage") },
                  { path: "files", ...page(() => import("@/features/ai/AiKnowledgePages"), "AiFilesPage") },
                  { path: "knowledge", ...page(() => import("@/features/ai/AiKnowledgePages"), "AiKnowledgePage") },
                  { path: "tasks", ...page(() => import("@/features/ai/AiTasksPages"), "AiTasksPage") },
                  { path: "tasks/:code", ...page(() => import("@/features/ai/AiTasksPages"), "AiTaskDetailPage") },
                  { path: "journal", ...page(() => import("@/features/ai/AiJournalPages"), "AiJournalPage") },
                  { path: "playground", ...page(() => import("@/features/ai/AiPlaygroundPage"), "AiPlaygroundPage") },
                  { path: "tests", ...page(() => import("@/features/ai/AiTestsPage"), "AiTestsPage") },
                  { path: "memory", ...page(() => import("@/features/ai/AiJournalPages"), "AiMemoryPage") },
                  { path: "posts", ...page(() => import("@/features/ai/AiPostsPage"), "AiPostsPage") },
                  { path: "sozlamalar", ...page(() => import("@/features/ai/AiSettingsPage"), "AiSettingsPage") },
                ],
              },
            ],
          },
          // Agency screens of this phase: the onboarding wizard (any member), the package and
          // credits, and the client import (owners, under Sozlamalar).
          {
            // The onboarding API is the owner's (CanManageTenantSettings), so the screen is too.
            element: <RequireRole allow={canManageTenantSettings} />,
            children: [
              { path: "/onboarding", ...page(() => import("@/features/agency/OnboardingPage"), "OnboardingPage") },
              { path: "/sozlamalar/tarif", ...page(() => import("@/features/agency/BillingPage"), "BillingPage") },
              { path: "/sozlamalar/import", ...page(() => import("@/features/agency/ImportPage"), "ImportPage") },
            ],
          },
          // The platform panel (Kapitan staff). The guard is the same one the integrations use.
          {
            element: <RequirePlatformAdmin />,
            children: [
              { path: "/platforma", element: <Navigate to="/platforma/tenantlar" replace /> },
              { path: "/platforma/tenantlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTenantsPage") },
              { path: "/platforma/tenantlar/:tenantId", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTenantPage") },
              { path: "/platforma/tariflar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTariffsPage") },
              { path: "/platforma/demo-sorovlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformDemoPage") },
              { path: "/platforma/kredit-sorovlari", ...page(() => import("@/features/platform/PlatformPages"), "PlatformCreditsPage") },
              { path: "/platforma/audit", ...page(() => import("@/features/platform/PlatformPages"), "PlatformAuditPage") },
              { path: "/platforma/provayderlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformProvidersPage") },
              { path: "/platforma/aeroportlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformAirportsPage") },
              { path: "/platforma/yonalishlar", ...page(() => import("@/features/platform/PlatformCataloguePages"), "PlatformDestinationsPage") },
              { path: "/platforma/kataloglar", ...page(() => import("@/features/platform/PlatformCataloguePages"), "PlatformCataloguesPage") },
              { path: "/platforma/support", ...page(() => import("@/features/platform/PlatformPages"), "PlatformSupportPage") },
            ],
          },
          // Kapitan staff only. Loaded on demand so the kit stays out of the main bundle.
          {
            element: <RequirePlatformAdmin />,
            children: [
              {
                path: "/ui-kit",
                ...page(() => import("@/features/ui-kit/UiKitPage"), "UiKitPage"),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
